import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, FileText, Scale, CreditCard, Shield, 
  Clock, AlertTriangle, CheckCircle2, Zap, Gift
} from 'lucide-react';

export default function CGV() {
  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <header className="border-b border-gray-800 py-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-orbitron text-xl font-bold text-cyan-400 glitch-subtle">
            L'EFFACEUR
          </Link>
          <Link to="/" className="text-gray-400 hover:text-cyan-400 flex items-center gap-2 text-sm font-mono">
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title */}
          <div className="flex items-center gap-4 mb-4">
            <FileText className="w-10 h-10 text-cyan-400" />
            <h1 className="font-orbitron text-3xl md:text-4xl font-bold text-white">
              CONDITIONS GÉNÉRALES <span className="text-cyan-400">DE VENTE</span>
            </h1>
          </div>
          <p className="text-gray-400 font-mono text-sm mb-12">// CONTRAT_UTILISATEUR_V2.0</p>
          
          <div className="space-y-8">
            {/* Préambule */}
            <section className="glass-card p-8 border border-gray-800">
              <h2 className="font-orbitron text-xl font-bold text-white mb-6">PRÉAMBULE</h2>
              <p className="text-gray-300 leading-relaxed">
                Les présentes Conditions Générales de Vente (CGV) régissent l'utilisation des services proposés
                par <strong className="text-cyan-400">L'EFFACEUR</strong>, entreprise individuelle immatriculée
                sous le SIRET 947 765 442 00019, dont le siège social est situé à Toulouse (Haute-Garonne).
                Contact : <a href="mailto:leffaceur@effaceurpro.fr" className="text-cyan-400 hover:underline">leffaceur@effaceurpro.fr</a>.
              </p>
              <p className="text-gray-400 mt-4">
                En utilisant nos services, l'Utilisateur reconnaît avoir pris connaissance et accepté 
                sans réserve les présentes CGV.
              </p>
            </section>
            
            {/* Article 1 - Services */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <Zap className="w-6 h-6 text-cyan-400" />
                <h2 className="font-orbitron text-xl font-bold text-white">ARTICLE 1 – OBJET DES SERVICES</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                L'EFFACEUR propose un service de représentation et d'assistance pour :
              </p>
              <ul className="space-y-2 text-gray-300 ml-4">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-1 flex-shrink-0" />
                  <span>L'identification et l'analyse des contrats et abonnements en cours</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-1 flex-shrink-0" />
                  <span>La résiliation des contrats non désirés auprès des prestataires</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-1 flex-shrink-0" />
                  <span>La négociation et souscription d'offres alternatives plus avantageuses</span>
                </li>
              </ul>
            </section>
            
            {/* Article 2 - Packs */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-6 h-6 text-magenta-400" />
                <h2 className="font-orbitron text-xl font-bold text-white">ARTICLE 2 – TARIFICATION</h2>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-gray-800/30 border border-gray-700 rounded">
                  <p className="text-cyan-400 font-mono font-bold">Pack "Mission Déménagement" — 149€ TTC</p>
                  <p className="text-gray-400 text-sm mt-1">Résiliation des contrats liés au logement (déménagement)</p>
                </div>
                <div className="p-4 bg-gray-800/30 border border-gray-700 rounded">
                  <p className="text-magenta-400 font-mono font-bold">Pack "Sérénité Succession" — 199€ TTC</p>
                  <p className="text-gray-400 text-sm mt-1">Clôture administrative complète (décès/héritage)</p>
                </div>
                <div className="p-4 bg-gray-800/30 border border-gray-700 rounded opacity-60">
                  <p className="text-gray-500 font-mono font-bold">Mission Audit IA — Prochainement disponible</p>
                  <p className="text-gray-600 text-sm mt-1">Rejoignez la liste d'attente depuis la page d'accueil</p>
                </div>
              </div>
            </section>
            
            {/* ARTICLE X - TRANSPARENCE (CRUCIAL) */}
            <section className="glass-card p-8 border-2 border-cyan-400/50 bg-cyan-400/5">
              <div className="flex items-center gap-3 mb-6">
                <Gift className="w-6 h-6 text-cyan-400" />
                <h2 className="font-orbitron text-xl font-bold text-white">
                  ARTICLE 3 – TRANSPARENCE DES REVENUS ET MÉCANISME DE GRATUITÉ
                </h2>
              </div>
              
              <div className="space-y-6 text-gray-300">
                <div>
                  <p className="text-cyan-400 font-mono font-bold mb-2">3.1. Rémunération de l'Utilisateur</p>
                  <p className="leading-relaxed">
                    Le service de base de L'EFFACEUR est facturé sous forme d'une <strong className="text-white">commission 
                    de succès s'élevant à 20 % (vingt pour cent) TTC des économies réelles</strong> identifiées 
                    et sécurisées sur la première année des contrats audités.
                  </p>
                </div>
                
                <div>
                  <p className="text-cyan-400 font-mono font-bold mb-2">3.2. Revenus d'Affiliation</p>
                  <p className="leading-relaxed">
                    L'Utilisateur est informé que L'EFFACEUR est partenaire de divers fournisseurs 
                    (Énergie, Télécoms, Assurances, etc.) et peut percevoir, à ce titre, une 
                    <strong className="text-white"> commission d'affiliation</strong> lors de la souscription 
                    d'un nouveau contrat pour le compte de l'Utilisateur.
                  </p>
                </div>
                
                <div>
                  <p className="text-cyan-400 font-mono font-bold mb-2">3.3. Mécanisme de Déduction Systématique</p>
                  <p className="leading-relaxed">
                    Dans un souci de <strong className="text-white">transparence totale</strong> et de 
                    préservation du pouvoir d'achat de ses Utilisateurs, L'EFFACEUR s'engage à 
                    <strong className="text-white"> déduire l'intégralité de la commission d'affiliation perçue</strong> du 
                    montant de sa propre commission de succès (les 20 % susmentionnés).
                  </p>
                </div>
                
                <div className="p-4 bg-cyan-400/10 border border-cyan-400/30 rounded-lg">
                  <p className="text-cyan-400 font-mono font-bold mb-3">3.4. Vers la gratuité du service</p>
                  <ul className="space-y-2 text-gray-200">
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-400">→</span>
                      <span>Si le montant de la commission d'affiliation est <strong>inférieur</strong> aux 20 % d'économies, 
                      l'Utilisateur ne paie que la différence restante.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-400">→</span>
                      <span>Si le montant de la commission d'affiliation est <strong>égal ou supérieur</strong> aux 20 % d'économies, 
                      <strong className="text-cyan-400"> le service est intégralement offert à l'Utilisateur (Frais de service : 0 €)</strong>.</span>
                    </li>
                  </ul>
                  <p className="text-gray-400 text-sm mt-4">
                    Cette déduction est calculée automatiquement par notre algorithme et apparaît de manière 
                    détaillée sur la facture finale transmise à l'Utilisateur.
                  </p>
                </div>
              </div>
            </section>
            
            {/* ARTICLE Y - RÉTRACTATION (CRUCIAL) */}
            <section className="glass-card p-8 border-2 border-orange-500/50 bg-orange-500/5">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="w-6 h-6 text-orange-400" />
                <h2 className="font-orbitron text-xl font-bold text-white">
                  ARTICLE 4 – RENONCIATION AU DROIT DE RÉTRACTATION
                </h2>
              </div>
              
              <div className="space-y-4 text-gray-300">
                <p className="leading-relaxed">
                  Conformément à l'article <strong className="text-orange-400">L221-28 du Code de la consommation</strong>, 
                  l'Utilisateur dispose normalement d'un délai de 14 jours pour exercer son droit de rétractation.
                </p>
                
                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <p className="text-orange-400 font-mono font-bold mb-2">CLAUSE DE RENONCIATION</p>
                  <p className="text-gray-200 leading-relaxed">
                    En validant le mandat de représentation, l'Utilisateur <strong>demande l'exécution immédiate 
                    de la prestation</strong> par l'IA de L'EFFACEUR et <strong>renonce expressément à son droit 
                    de rétractation de 14 jours</strong>, conformément à l'article L221-28 du Code de la consommation, 
                    afin de permettre le traitement instantané de ses dossiers.
                  </p>
                </div>
                
                <p className="text-gray-400 text-sm">
                  Cette renonciation est mentionnée explicitement lors de la signature électronique du mandat.
                </p>
                
                {/* Lien téléchargement formulaire */}
                <div className="mt-6 pt-4 border-t border-orange-500/30">
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/withdrawal-form`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/50 rounded hover:bg-orange-500/30 transition-colors"
                  >
                    <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-orange-400 text-sm font-medium">
                      Télécharger le Formulaire de Rétractation (PDF)
                    </span>
                  </a>
                </div>
              </div>
            </section>
            
            {/* Article Paiement */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-6 h-6 text-cyan-400" />
                <h2 className="font-orbitron text-xl font-bold text-white">ARTICLE 5 – MODALITÉS DE PAIEMENT</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                Les paiements sont effectués via la plateforme sécurisée <strong className="text-cyan-400">Stripe</strong>.
              </p>
              <ul className="space-y-2 text-gray-300 ml-4">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-1 flex-shrink-0" />
                  <span><strong>Packs fixes</strong> : Paiement intégral lors de la souscription</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-1 flex-shrink-0" />
                  <span><strong>Commission 20%</strong> : Facturation après confirmation des économies réalisées</span>
                </li>
              </ul>
              <p className="text-gray-400 text-sm mt-4">
                Moyens acceptés : Carte bancaire (Visa, Mastercard, American Express)
              </p>
            </section>
            
            {/* Article Protection données */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-magenta-400" />
                <h2 className="font-orbitron text-xl font-bold text-white">ARTICLE 6 – PROTECTION DES DONNÉES</h2>
              </div>
              <p className="text-gray-300 leading-relaxed">
                Les données personnelles sont traitées conformément au <strong className="text-magenta-400">RGPD</strong>.
              </p>
              <ul className="space-y-2 text-gray-300 ml-4 mt-4">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-magenta-400 mt-1 flex-shrink-0" />
                  <span>Chiffrement <strong className="text-cyan-400">AES-256</strong> de toutes les données sensibles</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-magenta-400 mt-1 flex-shrink-0" />
                  <span>Suppression automatique des documents <strong>30 jours</strong> après fin de mission</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-magenta-400 mt-1 flex-shrink-0" />
                  <span>Hébergement sécurisé sur <strong>Google Cloud Platform</strong></span>
                </li>
              </ul>
              <p className="text-gray-400 text-sm mt-4">
                Pour plus de détails, consultez notre <Link to="/confidentialite" className="text-cyan-400 hover:underline">Politique de Confidentialité</Link>.
              </p>
            </section>
            
            {/* Article Juridiction */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <Scale className="w-6 h-6 text-cyan-400" />
                <h2 className="font-orbitron text-xl font-bold text-white">ARTICLE 7 – DROIT APPLICABLE</h2>
              </div>
              <p className="text-gray-300 leading-relaxed">
                Les présentes CGV sont régies par le <strong className="text-cyan-400">droit français</strong>. 
                Tout litige relatif à leur interprétation ou à leur exécution relève de la compétence 
                exclusive des <strong className="text-cyan-400">tribunaux de Toulouse</strong>.
              </p>
            </section>
            
            {/* ARTICLE Z - CHARTE DE QUALITÉ (ENGAGEMENT PRESTATAIRE) */}
            <section className="glass-card p-8 border-2 border-success/50 bg-success/5">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-success" />
                <h2 className="font-orbitron text-xl font-bold text-white">
                  ARTICLE 8 – CHARTE DE QUALITÉ & ENGAGEMENT DE SERVICE
                </h2>
              </div>
              
              <div className="space-y-6 text-gray-300">
                <div>
                  <p className="text-success font-mono font-bold mb-2">8.1. Engagement de Neutralité</p>
                  <p className="leading-relaxed">
                    L'EFFACEUR s'engage à recommander en priorité <strong className="text-white">l'offre la plus 
                    économique pour l'Utilisateur</strong>, même si celle-ci ne génère aucune commission d'affiliation 
                    pour le prestataire. La neutralité du conseil est notre engagement premier.
                  </p>
                </div>
                
                <div>
                  <p className="text-success font-mono font-bold mb-2">8.2. Transparence des Sources</p>
                  <p className="leading-relaxed">
                    Les comparaisons de prix sont basées sur des <strong className="text-white">données vérifiées 
                    en temps réel</strong> issues de sources reconnues (Selectra, Ariase, UFC-Que Choisir, etc.). 
                    L'utilisateur est informé de la source et de la date de vérification de chaque offre.
                  </p>
                </div>
                
                <div>
                  <p className="text-success font-mono font-bold mb-2">8.3. Délai de Traitement</p>
                  <p className="leading-relaxed">
                    L'EFFACEUR s'engage à traiter chaque demande de résiliation dans un délai maximum de 
                    <strong className="text-cyan-400"> 48 heures ouvrées</strong> suivant la signature du mandat et 
                    la réception des documents nécessaires.
                  </p>
                </div>
                
                <div className="p-4 bg-success/10 border border-success/30 rounded-lg">
                  <p className="text-success font-mono font-bold mb-3">8.4. Garantie "Satisfait ou Remboursé"</p>
                  <p className="text-gray-200 leading-relaxed">
                    Pour les Packs forfaitaires (Mission Déménagement et Sérénité Succession), si aucune résiliation n'est 
                    obtenue dans les <strong>60 jours</strong> suivant la souscription du fait d'un manquement de 
                    L'EFFACEUR, le remboursement intégral est garanti sur simple demande.
                  </p>
                </div>
                
                <div>
                  <p className="text-success font-mono font-bold mb-2">8.5. Communication Proactive</p>
                  <p className="leading-relaxed">
                    L'Utilisateur est informé par email de l'avancement de chaque démarche et reçoit une 
                    <strong className="text-white"> preuve documentaire</strong> (accusé de réception, confirmation 
                    de résiliation) pour chaque action entreprise.
                  </p>
                </div>
              </div>
            </section>
          </div>
          
          {/* Footer */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm font-mono">
              Dernière mise à jour : Février 2026
            </p>
            <div className="flex items-center justify-center gap-4 mt-4">
              <Link 
                to="/mentions-legales" 
                className="text-cyan-400 hover:underline text-sm"
              >
                Mentions Légales
              </Link>
              <span className="text-gray-600">•</span>
              <Link 
                to="/confidentialite" 
                className="text-cyan-400 hover:underline text-sm"
              >
                Politique de Confidentialité
              </Link>
            </div>
          </div>
          
          {/* Toulouse Badge */}
          <div className="mt-8 text-center">
            <p className="text-magenta-400 font-mono text-sm">
              Fait avec le ❤️ à Toulouse
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
